import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Euro, DollarSign, Building2, Globe, AlertTriangle } from 'lucide-react';

interface EnforcementAction {
  date: string;
  authority: string;
  company: string;
  amount: string;
  description: string;
  violation: string;
}

interface RegionData {
  icon: React.ElementType;
  title: string;
  currency: string;
  actions: EnforcementAction[];
}

const regionData: RegionData[] = [
  {
    icon: Globe,
    title: 'Europe',
    currency: '€',
    actions: [
      {
        date: 'March 2025',
        authority: 'Irish DPC',
        company: 'Social Media Corp',
        amount: '265,000,000',
        description: 'Failure to implement appropriate technical and organizational measures to ensure data protection by design.',
        violation: 'GDPR Article 25'
      },
      {
        date: 'January 2025',
        authority: 'CNIL (France)',
        company: 'Tech Giant Inc',
        amount: '150,000,000',
        description: 'Insufficient legal basis for behavioral advertising and lack of easy opt-out mechanism.',
        violation: 'GDPR Articles 6 and 7'
      },
      {
        date: 'November 2024',
        authority: 'Spanish AEPD',
        company: 'Local Retail Chain',
        amount: '150,000',
        description: 'Improper handling of customer loyalty program data and inadequate privacy notices.',
        violation: 'GDPR Articles 13 and 14'
      },
      {
        date: 'October 2024',
        authority: 'Dutch DPA',
        company: 'HR Software Provider',
        amount: '75,000',
        description: 'Insufficient data retention policies and excessive collection of employee data.',
        violation: 'GDPR Articles 5 and 6'
      }
    ]
  },
  {
    icon: Globe,
    title: 'Americas',
    currency: '$',
    actions: [
      {
        date: 'February 2025',
        authority: 'FTC',
        company: 'Online Marketplace Inc',
        amount: '75,000,000',
        description: 'Deceptive data collection practices and failure to protect consumer information.',
        violation: 'FTC Act Section 5'
      },
      {
        date: 'December 2024',
        authority: 'California AG',
        company: 'Regional Healthcare Provider',
        amount: '85,000',
        description: 'Failure to properly secure patient portal and delayed breach notifications.',
        violation: 'CCPA & HIPAA'
      },
      {
        date: 'October 2024',
        authority: 'Virginia AG',
        company: 'E-commerce Platform',
        amount: '50,000',
        description: 'Non-compliant cookie consent mechanisms and failure to honor deletion requests.',
        violation: 'VCDPA Section 59.1-578'
      }
    ]
  },
  {
    icon: Globe,
    title: 'Asia',
    currency: '¥',
    actions: [
      {
        date: 'March 2025',
        authority: 'PIPC (South Korea)',
        company: 'Mobile App Developer',
        amount: '890,000,000',
        description: 'Unauthorized collection of personal information and inadequate security measures.',
        violation: 'PIPA'
      },
      {
        date: 'January 2025',
        authority: 'PDPC (Singapore)',
        company: 'Educational Technology Provider',
        amount: '65,000',
        description: 'Unauthorized collection of student data and failure to obtain parental consent.',
        violation: 'PDPA Sections 13 and 14'
      },
      {
        date: 'December 2024',
        authority: 'PIPC (South Korea)',
        company: 'Online Marketplace',
        amount: '45,000,000',
        description: 'Improper handling of customer payment information and inadequate vendor oversight.',
        violation: 'PIPA Article 29'
      }
    ]
  },
  {
    icon: Globe,
    title: 'Africa',
    currency: '$',
    actions: [
      {
        date: 'February 2025',
        authority: 'South Africa IR',
        company: 'Property Management Firm',
        amount: '150,000',
        description: 'Improper disposal of tenant records and unauthorized surveillance.',
        violation: 'POPIA Sections 19 and 20'
      },
      {
        date: 'January 2025',
        authority: 'Kenya DPC',
        company: 'Financial Services Provider',
        amount: '95,000',
        description: 'Unauthorized sharing of customer credit information with third parties.',
        violation: 'Data Protection Act Section 25'
      }
    ]
  },
  {
    icon: Globe,
    title: 'Oceania',
    currency: '$',
    actions: [
      {
        date: 'March 2025',
        authority: 'OAIC (Australia)',
        company: 'Real Estate Agency',
        amount: '125,000',
        description: 'Improper handling of rental applicant data and unauthorized disclosure.',
        violation: 'Privacy Act 1988 APP 11'
      },
      {
        date: 'January 2025',
        authority: 'NZ Privacy Commissioner',
        company: 'Retail Loyalty Program',
        amount: '85,000',
        description: 'Excessive data collection and failure to provide privacy choices.',
        violation: 'Privacy Act 2020 IPP 1 and 3'
      }
    ]
  }
];

export default function EnforcementActionsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold mb-4 relative font-montserrat text-gray-700">Privacy Enforcement Actions
            {/* Decorative underline */}
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-primary to-transparent opacity-75" />
            </h1>  
            <p className="text-lg text-gray-600">
              Recent privacy and data protection enforcement actions
            </p>
          </div>

          <div className="space-y-12">
            {regionData.map((region, index) => (
              <section key={index} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6 bg-gradient-to-r from-gray-50 to-white border-b">
                  <div className="flex items-center space-x-3">
                    <region.icon className="h-8 w-8 text-brand-primary" />
                    <h2 className="text-2xl font-semibold">{region.title}</h2>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Authority
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fine
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {region.actions.map((action, actionIndex) => (
                        <tr key={actionIndex} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {action.date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {action.authority}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {action.company}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="font-medium text-brand-primary">
                              {region.currency}{action.amount}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-md">
                            <div className="flex items-start space-x-2">
                              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                              <div>
                                <p>{action.description}</p>
                                <p className="text-xs text-gray-400 mt-1">Violation: {action.violation}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}